import { useEffect, useRef } from "react";
import { useCart } from "./CartContext";

// Guest/session identifier stored in sessionStorage (no cookies)
const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem("seritonex_session");
    if (!sid) {
      sid = uuid();
      sessionStorage.setItem("seritonex_session", sid);
    }
    return sid;
  } catch {
    return uuid();
  }
};

const uuid = () =>
  crypto?.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(16).slice(2)}`;

// Merge carts by id, summing quantities and preferring latest fields
const mergeCarts = (a = [], b = []) => {
  const map = new Map();
  const add = (list) => {
    for (const it of list) {
      const id = it.id ?? it.sku ?? it._id;
      if (!id) continue;
      const prev = map.get(id) || { ...it, quantity: 0 };
      const qty = (prev.quantity || 0) + (it.quantity || 1);
      map.set(id, { ...prev, ...it, id, quantity: qty });
    }
  };
  add(a);
  add(b);
  return Array.from(map.values());
};

export default function CartSync({ user, storageKey = "seritonex_cart" }) {
  const { cartItems: items, clearCart: emptyCart, addToCart: addItem, removeFromCart: removeItem } = useCart();
  const debounceRef = useRef();
  const logoutTimerRef = useRef();
  const hasMergedRef = useRef(false);
  const hasRestoredGuestRef = useRef(false);
  const prevUserRef = useRef(null);

  const readUserSnapshot = (uid) => {
    try {
      const raw = sessionStorage.getItem(`seritonex_cart_uid_${uid}`);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };
  const writeUserSnapshot = (uid, data) => {
    try {
      sessionStorage.setItem(`seritonex_cart_uid_${uid}`, JSON.stringify(data));
    } catch {}
  };

  // Ensure we have a session id for this browser session (non-PII)
  useEffect(() => {
    getSessionId();
  }, []);

  // On login, merge local cart (current items) with persisted cart. On logout, clear local cart.
  useEffect(() => {
    const prevUser = prevUserRef.current;
    prevUserRef.current = user;

    const run = async () => {
      // Logout transition: previous user existed, now null => clear cart and storage
      // Only clear if this is a genuine logout (not just auth initialization)
      if (prevUser && !user) {
        // Clear any pending logout timer
        if (logoutTimerRef.current) {
          clearTimeout(logoutTimerRef.current);
          logoutTimerRef.current = null;
        }

        // Only clear cart if user actually logged out intentionally
        const isLoggedIn = sessionStorage.getItem("isLoggedIn");
        
        // If isLoggedIn is null (removed) or "false", this is a real logout
        // If it's "true", this is just auth initialization - DON'T clear cart
        if (isLoggedIn !== "true") {
          // This is a real logout - clear cart
          try {
            emptyCart();
          } catch {}
          try {
            sessionStorage.removeItem(storageKey);
          } catch {}
          hasMergedRef.current = false;
        }
        return;
      }

      // Login transition: user present and we haven't merged yet
      if (!user || hasMergedRef.current) return;
      try {
        // Read user's stored cart from sessionStorage
        const localUserSnap = readUserSnapshot(user.uid);
        const localUserItems = Array.isArray(localUserSnap?.items)
          ? localUserSnap.items
          : [];

        const localItems = items || [];
        let merged = mergeCarts(localItems, localUserItems);

        // Replace local cart with merged snapshot
        emptyCart();
        if (merged.length) {
          for (const it of merged) {
            const qty = it.quantity || 1;
            const { quantity, ...rest } = it;
            addItem(rest, qty);
          }
        }

        // Persist to sessionStorage
        try {
          sessionStorage.setItem(storageKey, JSON.stringify({ items: merged }));
        } catch {}
        writeUserSnapshot(user.uid, { items: merged, updatedAt: Date.now() });
      } catch (e) {
        // ignore merge errors to not block UX
      } finally {
        hasMergedRef.current = true;
      }
    };
    run();
    // cleanup timer on effect rerun/unmount
    return () => {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // For guests (no user): restore from sessionStorage once per session if cart is empty
  useEffect(() => {
    if (user || hasRestoredGuestRef.current) return;
    hasRestoredGuestRef.current = true;

    try {
      if (!items || items.length === 0) {
        const raw = sessionStorage.getItem(storageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          const list = Array.isArray(parsed?.items) ? parsed.items : [];
          if (list.length) {
            for (const it of list) {
              const qty = it.quantity || 1;
              const { quantity, ...rest } = it;
              addItem(rest, qty);
            }
          }
        }
      }
    } catch (err) {
      console.error("Error restoring guest cart:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for admin-triggered removal of discontinued products across tabs
  useEffect(() => {
    const handler = (e) => {
      try {
        const id = e?.detail?.id;
        if (!id) return;
        // remove matching item from local cart
        try {
          removeItem(id);
        } catch {}
        // items changed effect will persist to sessionStorage
      } catch {}
    };
    window.addEventListener("cart:remove-product", handler);
    return () => window.removeEventListener("cart:remove-product", handler);
  }, [removeItem]);

  // Anytime cart changes, persist to sessionStorage; if user is logged in, also debounce sync
  useEffect(() => {
    // Only persist if we have actually initialized (prevent clearing on mount)
    if (!hasRestoredGuestRef.current && !hasMergedRef.current) return;

    // Persist to sessionStorage for immediate reloads within this browser session
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ items }));
    } catch {}

    if (!user) return; // guests only local persist

    // Debounce persisting per-user snapshot
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      writeUserSnapshot(user.uid, { items, updatedAt: Date.now() });
    }, 800);

    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [items, user, storageKey]);

  return null;
}
